import React from 'react';
import { IMaskInput } from 'react-imask';
import PropTypes from 'prop-types';

const MASK_CONFIGS = {
  name: {
    mask: /^[A-Za-zÀ-ÿ\s]+$/,
  },
  
  phone: {
    mask: '(00) 00000-0000',
  },
  
  cpf: {
    mask: '000.000.000-00',
  },
  
  creditCard: {
    mask: '0000 0000 0000 0000',
  },
  
  creditExpiration: {
    mask: '#0/y0',
    definitions: {
      '#': /[0-1]/,
      'y': /[2-9]/,
    },
  },
  
  date: {
    mask: Date,
    pattern: 'd{/}`m{/}`Y',
    format: (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return [day, month, year].join('/');
    },
    parse: (str) => {
      const [day, month, year] = str.split('/').map(Number);
      return new Date(year, month - 1, day);
    },
  },
  
  cvv: {
    mask: '0000',
  },
  
  licensePlate: {
    mask: 'aaa-0#00',
    definitions: {
      '#': /[0-9A-Za-z]/,
    },
    prepare: (str) => str.toUpperCase(),
  },
  
  zipCode: {
    mask: '00000-000',
    definitions: {
      '0': /[0-9]/
    }
  },
  
  document: {
    mask: [
      { mask: '000.000.000-00' },
      { mask: '00.000.000/0000-00' }
    ],
    dispatch: (appended, dynamicMasked) => {
      const document = (dynamicMasked.value + appended).replace(/\D/g, '');
      return dynamicMasked.compiledMasks[document.length > 11 ? 1 : 0];
    },
  },
};

const TextMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask="aaa-0#00"
      definitions={{
        '#': /[0-9A-Za-z]/,
      }}
      prepare={(str) => str.toUpperCase()}
      inputRef={ref}
      onAccept={(value, maskRef) => {
        onChange?.(maskRef.el.value);
      }}
      {...(ref ? { ref: (input) => {
        if (input?.inputElement) {
          ref(input.inputElement);
        }
      }} : {})}
      overwrite
    />
  );
});

TextMaskCustom.propTypes = {
  onChange: PropTypes.func,
};

TextMaskCustom.displayName = 'TextMaskCustom';

export const TextMaskLicensePlate = React.forwardRef(function TextMaskLicensePlate(props, ref) {
  return <TextMaskCustom {...props} ref={ref} />;
});

TextMaskLicensePlate.displayName = 'TextMaskLicensePlate';

const createMaskedInput = (maskType) => {
  const config = MASK_CONFIGS[maskType];
  
  return React.forwardRef(function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        {...config}
        inputRef={ref}
        onAccept={(value) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  });
};

export const TextMaskName = React.forwardRef(function TextMaskName(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={/^[a-zA-ZÀ-ÿ\s]*$/}
      definitions={{
        '#': /[a-zA-ZÀ-ÿ\s]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

export const TextMaskCPF = createMaskedInput('cpf');
export const TextMaskPhone = createMaskedInput('phone');
export const TextMaskCreditCard = createMaskedInput('creditCard');
export const TextMaskDate = createMaskedInput('date');
export const TextMaskCreditExpiration = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="00/00"
      definitions={{
        '0': /[0-9]/
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

export const TextMaskCreditCardCVV = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="000"
      definitions={{
        '0': /[0-9]/
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

export const TextMaskZipCode = createMaskedInput('zipCode');

TextMaskZipCode.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

TextMaskZipCode.displayName = 'TextMaskZipCode';

export const TextMaskDocument = React.forwardRef(function TextMaskDocument(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={[
        { mask: '000.000.000-00', maxLength: 14 },
        { mask: '00.000.000/0000-00', maxLength: 18 }
      ]}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});